import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useCart, formatPrice } from '../contexts/CartContext';
import { apiService } from '../services/api';
import { buildApiUrl } from '../config/api';

interface ShippingMethod { id: string; name: string; price: number; estimatedDays?: string; }

const CheckoutPage = () => {
  const { items, totalCents, count, clear } = useCart();
  const navigate = useNavigate();

  const [methods, setMethods] = useState<ShippingMethod[]>([]);
  const [methodId, setMethodId] = useState('');
  const [form, setForm] = useState({
    name: '', email: '', phone: '',
    address: '', city: '', postalCode: '', country: 'France',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Panier vide : on renvoie à la boutique (ex. accès direct à l'URL).
  useEffect(() => {
    if (count === 0) navigate('/panier', { replace: true });
  }, [count, navigate]);

  // Modes de livraison configurés dans l'admin.
  useEffect(() => {
    fetch(buildApiUrl('/settings/public'))
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const raw = data?.shippingMethods?.value;
        let parsed: ShippingMethod[] = [];
        try { parsed = typeof raw === 'string' ? JSON.parse(raw) : Array.isArray(raw) ? raw : []; } catch { parsed = []; }
        setMethods(parsed);
        if (parsed.length > 0) setMethodId(parsed[0].id);
      })
      .catch(() => setMethods([]));
  }, []);

  const selectedMethod = methods.find((m) => m.id === methodId);
  const shippingCents = selectedMethod?.price ?? 0;
  const grandTotal = totalCents + shippingCents;

  const set = (key: keyof typeof form, value: string) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!methodId) { setError('Choisissez un mode de livraison.'); return; }
    setSubmitting(true);
    setError('');

    const res = await apiService.checkout({
      items: items.map((i) => ({ productId: i.productId, quantity: i.quantity })),
      customer: { name: form.name, email: form.email, phone: form.phone || undefined },
      shipping: { address: form.address, city: form.city, postalCode: form.postalCode, country: form.country, methodId },
    });

    if (res.success && res.data?.checkoutUrl) {
      // Redirection vers la page de paiement Stripe. Le panier sera vidé au
      // retour sur la page de succès (après paiement confirmé).
      window.location.href = res.data.checkoutUrl;
    } else if (res.success && res.data?.requiresManualPayment) {
      clear();
      navigate(`/boutique/commande/succes?order=${res.data.orderNumber ?? ''}&manuel=1`);
    } else {
      setError(res.error || 'Une erreur est survenue lors de la commande.');
      setSubmitting(false);
    }
  };

  const inputClass = 'w-full px-4 py-3 rounded-lg bg-gray-900 border border-gray-700 text-white placeholder-gray-500 focus:border-sky-500 focus:outline-none';

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <Link to="/panier" className="inline-flex items-center gap-2 text-gray-400 hover:text-red-400 mb-6">
          <ArrowLeft className="w-4 h-4" /> Retour au panier
        </Link>

        <h1 className="text-3xl sm:text-4xl font-bold mb-8 bg-gradient-to-r from-red-400 to-sky-400 bg-clip-text text-transparent">
          Finaliser la commande
        </h1>

        <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Coordonnées + livraison */}
          <div className="lg:col-span-2 space-y-6">
            <section>
              <h2 className="font-semibold mb-3">Vos coordonnées</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input required placeholder="Nom complet" aria-label="Nom" value={form.name} onChange={(e) => set('name', e.target.value)} className={inputClass} />
                <input required type="email" placeholder="Email" aria-label="Email" value={form.email} onChange={(e) => set('email', e.target.value)} className={inputClass} />
                <input placeholder="Téléphone (facultatif)" aria-label="Téléphone" value={form.phone} onChange={(e) => set('phone', e.target.value)} className={`${inputClass} sm:col-span-2`} />
              </div>
            </section>

            <section>
              <h2 className="font-semibold mb-3">Adresse de livraison</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input required placeholder="Adresse" aria-label="Adresse" value={form.address} onChange={(e) => set('address', e.target.value)} className={`${inputClass} sm:col-span-2`} />
                <input required placeholder="Code postal" aria-label="Code postal" value={form.postalCode} onChange={(e) => set('postalCode', e.target.value)} className={inputClass} />
                <input required placeholder="Ville" aria-label="Ville" value={form.city} onChange={(e) => set('city', e.target.value)} className={inputClass} />
                <input required placeholder="Pays" aria-label="Pays" value={form.country} onChange={(e) => set('country', e.target.value)} className={`${inputClass} sm:col-span-2`} />
              </div>
            </section>

            <section>
              <h2 className="font-semibold mb-3">Mode de livraison</h2>
              {methods.length === 0 ? (
                <p className="text-gray-500 text-sm">Aucun mode de livraison disponible pour le moment.</p>
              ) : (
                <div className="space-y-2">
                  {methods.map((m) => (
                    <label key={m.id} className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-colors ${methodId === m.id ? 'border-sky-500 bg-sky-500/10' : 'border-gray-700 hover:border-gray-600'}`}>
                      <span className="flex items-center gap-3">
                        <input type="radio" name="shipping" checked={methodId === m.id} onChange={() => setMethodId(m.id)} />
                        <span>
                          <span className="font-medium">{m.name}</span>
                          {m.estimatedDays && <span className="text-gray-400 text-sm"> — {m.estimatedDays}</span>}
                        </span>
                      </span>
                      <span className="font-semibold">{m.price === 0 ? 'Gratuit' : formatPrice(m.price)}</span>
                    </label>
                  ))}
                </div>
              )}
            </section>
          </div>

          {/* Récapitulatif */}
          <div className="lg:col-span-1">
            <div className="p-6 border border-gray-800 rounded-xl bg-gray-900/30 sticky top-6">
              <h2 className="font-semibold mb-4">Récapitulatif</h2>
              <div className="space-y-2 text-sm">
                {items.map((i) => (
                  <div key={i.productId} className="flex justify-between text-gray-300">
                    <span className="truncate mr-2">{i.name} × {i.quantity}</span>
                    <span>{formatPrice(i.priceAmount * i.quantity)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-800 mt-4 pt-4 space-y-2 text-sm">
                <div className="flex justify-between text-gray-400"><span>Sous-total</span><span>{formatPrice(totalCents)}</span></div>
                <div className="flex justify-between text-gray-400"><span>Livraison</span><span>{shippingCents === 0 ? '—' : formatPrice(shippingCents)}</span></div>
                <div className="flex justify-between text-lg font-bold pt-2"><span>Total</span><span>{formatPrice(grandTotal)}</span></div>
              </div>

              {error && <p className="text-red-400 text-sm mt-4">{error}</p>}

              <button
                type="submit"
                disabled={submitting || methods.length === 0}
                className="mt-5 w-full px-6 py-3 rounded-full bg-gradient-to-r from-red-500 to-sky-500 text-white font-semibold hover:shadow-xl hover:shadow-sky-500/30 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? 'Redirection…' : 'Payer'}
              </button>
              <p className="text-gray-500 text-xs mt-3 text-center">
                Paiement sécurisé par Stripe. Aucune donnée bancaire ne transite par ce site.
              </p>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CheckoutPage;
